import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { TextField } from '@material-ui/core';
import useVrackApi from '../../../hooks/useVrackApi';

SnapshotName.propTypes = {
  snapshotName: PropTypes.string,
  updateSnapshotName: PropTypes.func,
  handleSnapshotNameValidationChange: PropTypes.func
};

// the regex rule to validate the snapshot name. this ensures the name is all lowercase, starts & ends with alphanumeric, and contains no spaces
const snapshotNameMaxLength = 40;
const validSnapshotName = new RegExp(
  `^(?![0-9]+$)(?!.*-$)(?!-)[a-z0-9-]{1,${snapshotNameMaxLength}}$`
);
const snapshotNameInputHelperText = 'The name you wish to use for your snapshot';
const snapshotNameInputSuccessHelperText = 'Snapshot name is valid';
const snapshotNameInputErrEmptyText = "Snapshot name can't be empty";
const snapshotNameTooLongErrHelperText = 'Snapshot name must contain no more than 40 characters';
const snapshotNameInputErrHelperText = `Snapshot name must contain only lowercase alphanumeric characters or '-', and start and end with an alphanumeric number.`;
const snapshotNameAlreadyExistsHelperText = `Snapshot with that name already exists`;

export default function SnapshotName({
  snapshotName,
  updateSnapshotName,
  handleSnapshotNameValidationChange
}) {
  const [snapshotNameHelperText, setSnapshotNameHelperText] = useState(snapshotNameInputHelperText);
  const [snapshotNameError, setSnapshotNameError] = useState(false);
  const [typingTimer, setTypingTimer] = useState(null);
  const { fetchData: vrackApiValidateSnapshotName } = useVrackApi(
    'get',
    `/racks/snapshot/${snapshotName}`
  );

  useEffect(() => {
    handleSnapshotNameValidationChange(false);
    validateInputs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validateInputs = () => {
    if (snapshotName !== '') {
      validateSnapshotName(snapshotName);
    }
  };

  const validateSnapshotName = (snapshotName) => {
    // checks if it meets length criteria
    if (validSnapshotName.test(snapshotName)) {
      // succeeds regex check for kubernetes naming convention
      setSnapshotNameHelperText(snapshotNameInputSuccessHelperText);
      setSnapshotNameError(false);
      updateSnapshotName(snapshotName);
      handleSnapshotNameValidationChange(true);
    } else {
      // set err message if invalid
      if (snapshotName.length > snapshotNameMaxLength) {
        setSnapshotNameHelperText(snapshotNameTooLongErrHelperText);
      } else if (snapshotName === '') {
        setSnapshotNameHelperText(snapshotNameInputErrEmptyText);
      } else {
        setSnapshotNameHelperText(snapshotNameInputErrHelperText);
      }
      setSnapshotNameError(true);
    }
  };

  const handleSnapshotNameChange = (event) => {
    // Set button to disabled until validation is completed
    handleSnapshotNameValidationChange(false);
    const snapshotName = event.target.value;

    clearTimeout(typingTimer);
    setTypingTimer(
      setTimeout(async () => {
        console.log('Checking if snapshot name exists');
        // Call checkSnapshotExists then call remaining checks afterwards
        vrackApiValidateSnapshotName({
          url: `/racks/snapshot/${snapshotName}`
        })
          .then(() => {
            // Snapshot Exists
            setSnapshotNameHelperText(snapshotNameAlreadyExistsHelperText);
            setSnapshotNameError(true);
          })
          .catch(() => {
            validateSnapshotName(snapshotName);
          });
      }, 500)
    );
  };

  return (
    <TextField
      inputProps={{ 'data-testid': 'snapshot-name' }}
      fullWidth
      required
      label="Snapshot Name"
      defaultValue={snapshotName}
      helperText={snapshotNameHelperText}
      onChange={handleSnapshotNameChange}
      error={snapshotNameError}
    />
  );
}
